/**
 * Lightweight ambient firefly group — hover, blink warning, scatter, return.
 */

import type {
  FireflyHomeBounds,
  FireflyScatterBounds,
  FireflyYardConfig,
} from '@/world-prototype/ambience/fireflyYards';
import { fireflyHomeCenter } from '@/world-prototype/ambience/fireflyYards';
import type { SpawnPoint } from '@/world-prototype/types';

export type FireflyGroupPhase =
  | 'ambient'
  | 'warningBlink'
  | 'scattering'
  | 'dispersed'
  | 'returning';

export const FIREFLY_TIMING = {
  warningBlinkMs: 2_200,
  scatterMs: 8_800,
  dispersedHoldMs: 6_000,
  returnMs: 9_400,
  retriggerCooldownMs: 3_600,
} as const;

/** Minimum spacing between fireflies at spawn (world-width units). */
export const FIREFLY_MIN_HOME_SEPARATION = 0.014;

/** Forgiving player proximity radius in world units. */
export const FIREFLY_PROXIMITY_RADIUS = 0.085;

export interface FireflyInstance {
  id: string;
  homeOffsetX: number;
  homeOffsetY: number;
  x: number;
  y: number;
  scatterX: number;
  scatterY: number;
  scatterFromX: number;
  scatterFromY: number;
  /** 0–1 stagger before this fly begins scattering. */
  scatterDelayT: number;
  pulsePhase: number;
  driftPhase: number;
  /** Independent ambient shimmer rate (rad/ms). */
  pulseRate: number;
  /** Independent warning shimmer rate (rad/ms). */
  warningRate: number;
  /** Per-fly base brightness (0.48–0.92). */
  glowBias: number;
  /** Display size multiplier for glow depth (0.72–1.18). */
  sizeScale: number;
  /** Second drift oscillator for curved paths. */
  driftPhase2: number;
  /** Ambient arc speed (rad/ms). */
  driftSpeed: number;
  /** Max wander radius in world units. */
  orbitRadius: number;
  /** Lagged position for soft motion impression. */
  trailX: number;
  trailY: number;
}

export interface FireflyGroupState {
  yardId: string;
  homeX: number;
  homeY: number;
  homeSpread: number;
  homeBounds: FireflyHomeBounds | null;
  scatterBounds: FireflyScatterBounds;
  phase: FireflyGroupPhase;
  timerMs: number;
  moveT: number;
  cooldownMs: number;
  instances: FireflyInstance[];
}

export function createFireflyGroup(
  config: FireflyYardConfig,
  rng: () => number = Math.random,
): FireflyGroupState {
  const center = fireflyHomeCenter(config);
  const homeBounds = config.homeBounds ?? null;
  const homes = sampleSpreadHomePositions(config, config.count, rng);
  const instances: FireflyInstance[] = [];
  for (let i = 0; i < homes.length; i += 1) {
    const home = homes[i]!;
    const offset = {
      x: home.x - center.x,
      y: home.y - center.y,
    };
    instances.push({
      id: `${config.yardId}-fly-${i}`,
      homeOffsetX: offset.x,
      homeOffsetY: offset.y,
      x: home.x,
      y: home.y,
      scatterX: home.x,
      scatterY: home.y,
      scatterFromX: home.x,
      scatterFromY: home.y,
      scatterDelayT:
        (i / Math.max(1, config.count - 1)) * 0.58 + rng() * 0.18,
      pulsePhase: rng() * Math.PI * 2,
      driftPhase: rng() * Math.PI * 2,
      pulseRate: 0.00042 + rng() * 0.00085,
      warningRate: 0.0012 + rng() * 0.0024,
      glowBias: 0.52 + rng() * 0.44,
      sizeScale: 0.78 + rng() * 0.46,
      driftPhase2: rng() * Math.PI * 2,
      driftSpeed: 0.00014 + rng() * 0.00014,
      orbitRadius: orbitRadiusForFly(homeBounds, config, rng),
      trailX: home.x,
      trailY: home.y,
    });
  }
  return {
    yardId: config.yardId,
    homeX: center.x,
    homeY: center.y,
    homeSpread: homeBounds
      ? Math.max(
          (homeBounds.maxX - homeBounds.minX) * 0.5,
          (homeBounds.maxY - homeBounds.minY) * 0.5,
        )
      : config.home.spread,
    homeBounds,
    scatterBounds: config.scatterBounds,
    phase: 'ambient',
    timerMs: 0,
    moveT: 0,
    cooldownMs: 0,
    instances,
  };
}

export function stepFireflyGroup(
  state: FireflyGroupState,
  dtMs: number,
  player: SpawnPoint | null,
  worldAspect: number,
  nowMs: number,
  rng: () => number = Math.random,
): void {
  if (state.cooldownMs > 0) {
    state.cooldownMs = Math.max(0, state.cooldownMs - dtMs);
  }

  if (state.phase === 'ambient') {
    stepAmbient(state, nowMs);
    if (
      player &&
      state.cooldownMs <= 0 &&
      playerDistance(player, state.homeX, state.homeY, worldAspect) <
        FIREFLY_PROXIMITY_RADIUS
    ) {
      beginWarningBlink(state);
    }
    return;
  }

  state.timerMs -= dtMs;

  switch (state.phase) {
    case 'warningBlink':
      if (state.timerMs <= 0) beginScatter(state, rng);
      return;
    case 'scattering':
      state.moveT = clamp01(state.moveT + dtMs / FIREFLY_TIMING.scatterMs);
      updateScatterPositions(state);
      if (state.moveT >= 1) {
        state.phase = 'dispersed';
        state.timerMs = FIREFLY_TIMING.dispersedHoldMs;
        state.moveT = 0;
      }
      return;
    case 'dispersed':
      stepDispersed(state, nowMs);
      if (state.timerMs <= 0) beginReturn(state);
      return;
    case 'returning':
      state.moveT = clamp01(state.moveT + dtMs / FIREFLY_TIMING.returnMs);
      updateReturnPositions(state);
      if (state.moveT >= 1) finishReturn(state);
      return;
    default:
      return;
  }
}

export function fireflyOpacity(
  state: FireflyGroupState,
  instance: FireflyInstance,
  nowMs: number,
): number {
  const t = nowMs * instance.pulseRate + instance.pulsePhase;
  const ambientPulse =
    instance.glowBias +
    0.04 * Math.sin(t) +
    0.02 * Math.sin(t * 0.37 + instance.driftPhase) +
    0.012 * Math.sin(t * 1.63 + instance.pulsePhase * 0.5);

  if (state.phase === 'warningBlink') {
    const shimmer =
      0.9 +
      0.08 *
        Math.sin(
          nowMs * instance.warningRate + instance.pulsePhase * 1.63,
        );
    const urgency =
      0.94 +
      0.06 *
        (1 - clamp01(state.timerMs / FIREFLY_TIMING.warningBlinkMs));
    return clamp01(ambientPulse * shimmer * urgency);
  }

  if (state.phase === 'scattering') {
    const travelPulse =
      0.92 +
      0.06 *
        Math.sin(nowMs * instance.pulseRate * 1.1 + instance.pulsePhase);
    return clamp01(ambientPulse * travelPulse);
  }

  return clamp01(ambientPulse);
}

/** Gentle per-fly breathing scale for a dreamy ambient feel. */
export function fireflyGlowScale(
  state: FireflyGroupState,
  instance: FireflyInstance,
  nowMs: number,
): number {
  const breathe =
    1 + 0.028 * Math.sin(nowMs * instance.pulseRate * 0.9 + instance.driftPhase);
  if (state.phase === 'warningBlink') {
    const shimmer =
      1 +
      0.032 *
        Math.sin(nowMs * instance.warningRate + instance.pulsePhase * 0.9);
    return breathe * shimmer;
  }
  return breathe;
}

/** Per-fly size multiplier for cluster depth. */
export function fireflySizeScale(instance: FireflyInstance): number {
  return instance.sizeScale;
}

function beginWarningBlink(state: FireflyGroupState): void {
  state.phase = 'warningBlink';
  state.timerMs = FIREFLY_TIMING.warningBlinkMs;
}

function beginScatter(state: FireflyGroupState, rng: () => number): void {
  state.phase = 'scattering';
  state.moveT = 0;
  for (const fly of state.instances) {
    fly.scatterFromX = fly.x;
    fly.scatterFromY = fly.y;
    const target = pickScatterTarget(state, fly, rng);
    fly.scatterX = target.x;
    fly.scatterY = target.y;
  }
}

function beginReturn(state: FireflyGroupState): void {
  state.phase = 'returning';
  state.moveT = 0;
  for (const fly of state.instances) {
    fly.scatterX = fly.x;
    fly.scatterY = fly.y;
  }
}

function finishReturn(state: FireflyGroupState): void {
  state.phase = 'ambient';
  state.timerMs = 0;
  state.moveT = 0;
  state.cooldownMs = FIREFLY_TIMING.retriggerCooldownMs;
  for (const fly of state.instances) {
    fly.x = state.homeX + fly.homeOffsetX;
    fly.y = state.homeY + fly.homeOffsetY;
    fly.scatterX = fly.x;
    fly.scatterY = fly.y;
  }
}

function stepAmbient(state: FireflyGroupState, nowMs: number): void {
  for (const fly of state.instances) {
    const baseX = state.homeX + fly.homeOffsetX;
    const baseY = state.homeY + fly.homeOffsetY;
    const t = nowMs * fly.driftSpeed + fly.driftPhase;
    const t2 = nowMs * fly.driftSpeed * 0.71 + fly.driftPhase2;
    const r = fly.orbitRadius;
    const driftX =
      Math.sin(t * 0.58) * r + Math.sin(t2 * 0.93) * r * 0.42;
    const driftY =
      Math.cos(t * 0.49) * r * 0.82 +
      Math.sin(t2 * 0.61) * r * 0.34 +
      Math.sin(t * 1.12 + fly.driftPhase2) * r * 0.16;
    const next = clampToHomeBounds(
      { x: baseX + driftX, y: baseY + driftY },
      state,
    );
    fly.x = next.x;
    fly.y = next.y;
    stepFireflyTrail(fly);
  }
}

function stepDispersed(state: FireflyGroupState, nowMs: number): void {
  for (const fly of state.instances) {
    const t = nowMs * fly.driftSpeed * 0.85 + fly.driftPhase;
    const t2 = nowMs * fly.driftSpeed * 0.62 + fly.driftPhase2;
    const r = fly.orbitRadius * 0.55;
    fly.x =
      fly.scatterX +
      Math.sin(t * 0.58) * r +
      Math.sin(t2 * 0.88) * r * 0.35;
    fly.y =
      fly.scatterY +
      Math.cos(t * 0.51) * r * 0.75 +
      Math.sin(t2 * 0.47) * r * 0.28;
    stepFireflyTrail(fly);
  }
}

function stepFireflyTrail(fly: FireflyInstance): void {
  fly.trailX += (fly.x - fly.trailX) * 0.1;
  fly.trailY += (fly.y - fly.trailY) * 0.1;
}

function orbitRadiusForFly(
  homeBounds: FireflyHomeBounds | null,
  config: FireflyYardConfig,
  rng: () => number,
): number {
  if (homeBounds) {
    const w = homeBounds.maxX - homeBounds.minX;
    const h = homeBounds.maxY - homeBounds.minY;
    return (0.12 + rng() * 0.18) * Math.max(w, h * 0.85);
  }
  return config.home.spread * (0.18 + rng() * 0.14);
}

function updateScatterPositions(state: FireflyGroupState): void {
  const globalT = state.moveT;
  for (const fly of state.instances) {
    const span = Math.max(0.18, 1 - fly.scatterDelayT * 0.55);
    const localT = clamp01((globalT - fly.scatterDelayT) / span);
    const eased = easeOutSine(localT);
    fly.x = lerp(fly.scatterFromX, fly.scatterX, eased);
    fly.y = lerp(fly.scatterFromY, fly.scatterY, eased);
    stepFireflyTrail(fly);
  }
}

function updateReturnPositions(state: FireflyGroupState): void {
  const globalT = easeInOutSine(state.moveT);
  for (const fly of state.instances) {
    const toX = state.homeX + fly.homeOffsetX;
    const toY = state.homeY + fly.homeOffsetY;
    const delay = fly.scatterDelayT * 0.35;
    const localT = clamp01((globalT - delay) / Math.max(0.2, 1 - delay));
    const eased = easeInOutSine(localT);
    fly.x = lerp(fly.scatterX, toX, eased);
    fly.y = lerp(fly.scatterY, toY, eased);
    stepFireflyTrail(fly);
  }
}

function pickScatterTarget(
  state: FireflyGroupState,
  fly: FireflyInstance,
  rng: () => number,
): SpawnPoint {
  const bounds = state.scatterBounds;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const angle = rng() * Math.PI * 2;
    const dist = 0.08 + rng() * 0.14;
    const candidate = {
      x: fly.scatterFromX + Math.cos(angle) * dist,
      y: fly.scatterFromY + Math.sin(angle) * dist * 0.75,
    };
    if (
      candidate.x >= bounds.minX &&
      candidate.x <= bounds.maxX &&
      candidate.y >= bounds.minY &&
      candidate.y <= bounds.maxY
    ) {
      return candidate;
    }
  }
  return {
    x: bounds.minX + rng() * (bounds.maxX - bounds.minX),
    y: bounds.minY + rng() * (bounds.maxY - bounds.minY),
  };
}

function sampleSpreadHomePositions(
  config: FireflyYardConfig,
  count: number,
  rng: () => number,
): SpawnPoint[] {
  if (config.homeBounds) {
    return sampleSpreadInBounds(config.homeBounds, count, rng);
  }
  const points: SpawnPoint[] = [];
  const maxAttempts = count * 60;
  let attempts = 0;
  while (points.length < count && attempts < maxAttempts) {
    attempts += 1;
    const candidate = sampleHomePosition(config, rng);
    if (
      points.every(
        (point) => homeSeparation(point, candidate) >= FIREFLY_MIN_HOME_SEPARATION,
      )
    ) {
      points.push(candidate);
    }
  }
  while (points.length < count) {
    points.push(sampleHomePosition(config, rng));
  }
  return points;
}

/** Poisson-style scatter inside a home rectangle with minimum spacing. */
function sampleSpreadInBounds(
  bounds: FireflyHomeBounds,
  count: number,
  rng: () => number,
): SpawnPoint[] {
  const points: SpawnPoint[] = [];
  const active: SpawnPoint[] = [];
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxY - bounds.minY;
  const first: SpawnPoint = {
    x: bounds.minX + rng() * w,
    y: bounds.minY + rng() * h,
  };
  points.push(first);
  active.push(first);

  while (points.length < count && active.length > 0) {
    const idx = Math.floor(rng() * active.length);
    const center = active[idx]!;
    let placed = false;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const angle = rng() * Math.PI * 2;
      const dist =
        FIREFLY_MIN_HOME_SEPARATION * (1 + rng() * 0.85);
      const candidate = {
        x: center.x + Math.cos(angle) * dist,
        y: center.y + Math.sin(angle) * dist * 0.82,
      };
      if (
        candidate.x < bounds.minX ||
        candidate.x > bounds.maxX ||
        candidate.y < bounds.minY ||
        candidate.y > bounds.maxY
      ) {
        continue;
      }
      if (
        points.every(
          (point) =>
            homeSeparation(point, candidate) >= FIREFLY_MIN_HOME_SEPARATION,
        )
      ) {
        points.push(candidate);
        active.push(candidate);
        placed = true;
        break;
      }
    }
    if (!placed) active.splice(idx, 1);
  }

  while (points.length < count) {
    const candidate = {
      x: bounds.minX + rng() * w,
      y: bounds.minY + rng() * h,
    };
    if (
      points.every(
        (point) =>
          homeSeparation(point, candidate) >= FIREFLY_MIN_HOME_SEPARATION * 0.72,
      )
    ) {
      points.push(candidate);
    } else {
      points.push(candidate);
    }
  }
  return points;
}

function homeSeparation(a: SpawnPoint, b: SpawnPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function sampleHomePosition(
  config: FireflyYardConfig,
  rng: () => number,
): SpawnPoint {
  if (config.homeBounds) {
    return {
      x:
        config.homeBounds.minX +
        rng() * (config.homeBounds.maxX - config.homeBounds.minX),
      y:
        config.homeBounds.minY +
        rng() * (config.homeBounds.maxY - config.homeBounds.minY),
    };
  }
  const offset = sampleHomeOffset(config.home.spread, rng);
  return {
    x: config.home.x + offset.x,
    y: config.home.y + offset.y,
  };
}

function clampToHomeBounds(
  point: SpawnPoint,
  state: FireflyGroupState,
): SpawnPoint {
  if (!state.homeBounds) return point;
  return {
    x: Math.max(state.homeBounds.minX, Math.min(state.homeBounds.maxX, point.x)),
    y: Math.max(state.homeBounds.minY, Math.min(state.homeBounds.maxY, point.y)),
  };
}

function sampleHomeOffset(
  spread: number,
  rng: () => number,
): SpawnPoint {
  const angle = rng() * Math.PI * 2;
  const dist = rng() * spread;
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist * 0.75,
  };
}

function playerDistance(
  player: SpawnPoint,
  x: number,
  y: number,
  worldAspect: number,
): number {
  const dx = player.x - x;
  const dy = (player.y - y) / (worldAspect > 0 ? worldAspect : 1);
  return Math.hypot(dx, dy);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function easeOutSine(t: number): number {
  return Math.sin((clamp01(t) * Math.PI) / 2);
}

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * clamp01(t)) - 1) / 2;
}
